import { useEffect, useMemo, useRef } from 'react';
// @ts-expect-error
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
// @ts-expect-error
import View from '@arcgis/core/views/MapView';
import { useDispatch, useSelector } from 'react-redux';

import type { AppDispatch } from 'state/store';

import { getDefectFeatures } from 'domain/surveyDataManager/api/features';
import { getDefectRules } from 'domain/surveyDataManager/api/rulesets';
import type { DefectFeaturesResponseBody } from 'domain/surveyDataManager/contracts/defectFeatures';
import type { DefectRule } from 'domain/surveyDataManager/contracts/defectRulesets';
import { isDefectOverlayLayerKind } from 'domain/surveyDataManager/defectOverlays/overlayKinds';
import { createDefectGeoJsonLayerConfig } from 'domain/surveyDataManager/map/createDefectGeoJsonLayer';
import { buildDefectRulesRenderer } from 'domain/surveyDataManager/map/buildDefectRulesRenderer';
import {
  inferGeometryKindFromFeatures,
  type DefectGeometryKind,
} from 'domain/surveyDataManager/map/defectGeometry';
import { revokeGeoJsonLayerUrl } from 'domain/surveyDataManager/map/geoJsonBlobUrl';
import { toActiveDefectMapLayerId } from 'domain/surveyDataManager/map/activeLayerMapId';
import { offsetDefectFeaturesMetres } from 'domain/surveyDataManager/map/offsetGeoJsonFeatures';
import { assignSameLaneOffsetMetres } from 'domain/surveyDataManager/map/sameLaneOffset';
import { settingsToDefectRules } from 'domain/surveyDataManager/visualization/activeLayerVisualizationSettings';
import {
  filterFeaturesToWorstCondition,
  filterRulesToWorstCondition,
  isWorstConditionRuleset,
} from 'domain/surveyDataManager/visualization/worstConditionOnly';
import { filterFeaturesByRecipeFilter } from 'domain/surveyDataManager/visualization/recipeFeatureFilter';
import type {
  ActiveDefectLayer,
  ActiveDefectLayerId,
} from 'state/surveyDataManager/activeLayersSlice';
import { selectActiveDefectLayers, selectPendingMapLayerIds } from 'state/surveyDataManager';
import { layerMapRenderSettled } from 'state/surveyDataManager/uiSlice';

interface TrackedDefectLayer {
  layer: InstanceType<typeof GeoJSONLayer>;
  blobUrl: string;
  rules: readonly DefectRule[];
  geometryKind: DefectGeometryKind;
  offsetMetres: number;
}

function activeLayerDataSignature(layers: readonly ActiveDefectLayer[]): string {
  return layers
    .map(
      (layer) =>
        `${layer.id}:m${layer.measurementSlug}:y${layer.surveyYearSlug}:l${layer.lane}:f${JSON.stringify(layer.featureFilter ?? null)}`,
    )
    .join('|');
}

function activeLayerSameLaneOffsetSignature(
  layers: readonly ActiveDefectLayer[],
): string {
  const offsets = assignSameLaneOffsetMetres(layers);
  return layers
    .map((layer) => `${layer.id}:${offsets.get(layer.id) ?? 0}`)
    .join('|');
}

function activeLayerPresentationSignature(layers: readonly ActiveDefectLayer[]): string {
  return layers
    .map(
      (layer) =>
        `${layer.id}:v${layer.visible ? '1' : '0'}:o${layer.opacity}:h${layer.highlighted ? '1' : '0'}:w${layer.lineWidth}:s${layer.lineStyle}:n${layer.displayName}`,
    )
    .join('|');
}

function activeLayerVisualizationSignature(layers: readonly ActiveDefectLayer[]): string {
  return layers
    .map((layer) => {
      const settings = layer.visualizationSettings;
      if (!settings || settings.measurementSlug !== layer.measurementSlug) {
        return `${layer.id}:m${layer.measurementSlug}:none`;
      }
      return `${layer.id}:m${layer.measurementSlug}:${settings.colorLow}:${settings.colorHigh}:${settings.thresholds.join(',')}:${settings.ruleName}`;
    })
    .join('|');
}

function resolveActiveLayerRules(
  layerMeta: ActiveDefectLayer,
  apiRules: readonly DefectRule[],
): readonly DefectRule[] {
  const settings = layerMeta.visualizationSettings;
  const baseRules =
    settings && settings.measurementSlug === layerMeta.measurementSlug
      ? settingsToDefectRules(settings, undefined, {
          rulesetSlug: String(layerMeta.rulesetSlug),
        })
      : apiRules;

  if (isWorstConditionRuleset(String(layerMeta.rulesetSlug))) {
    return filterRulesToWorstCondition(baseRules);
  }

  const bandSlugs = layerMeta.featureFilter?.bandSlugs;
  if (bandSlugs != null && bandSlugs.length > 0) {
    const allowed = new Set(bandSlugs.map((slug) => slug.trim().toLowerCase()));
    return baseRules.filter((rule) => allowed.has(rule.slug.trim().toLowerCase()));
  }

  return baseRules;
}

function resolveActiveLayerFeatures(
  layerMeta: ActiveDefectLayer,
  features: DefectFeaturesResponseBody,
): DefectFeaturesResponseBody {
  let resolved = features;

  if (layerMeta.featureFilter != null) {
    resolved = filterFeaturesByRecipeFilter(resolved, layerMeta.featureFilter);
  }

  if (!isWorstConditionRuleset(String(layerMeta.rulesetSlug))) {
    return resolved;
  }

  const thresholds =
    layerMeta.visualizationSettings?.measurementSlug === layerMeta.measurementSlug
      ? layerMeta.visualizationSettings.thresholds
      : [];

  return filterFeaturesToWorstCondition(resolved, thresholds);
}

function getLayerLoadErrorMessage(layer: InstanceType<typeof GeoJSONLayer>): string {
  const loadError = layer.loadError as { message?: string } | null | undefined;
  return loadError?.message ?? 'Unknown layer load error';
}

async function waitForLayerReady(
  mapView: View,
  layer: InstanceType<typeof GeoJSONLayer>,
): Promise<void> {
  await layer.when();
  if (layer.loadStatus === 'failed') {
    throw new Error(getLayerLoadErrorMessage(layer));
  }
  if (typeof layer.load === 'function') {
    await layer.load();
  }
  if (layer.loadStatus === 'failed') {
    throw new Error(getLayerLoadErrorMessage(layer));
  }
  const layerView = await mapView.whenLayerView(layer);
  await layerView.when();
}

async function zoomMapToLayer(
  mapView: View,
  layer: InstanceType<typeof GeoJSONLayer>,
): Promise<void> {
  let extent = layer.fullExtent;
  if (!extent && typeof layer.queryExtent === 'function') {
    const queryResult = await layer.queryExtent();
    extent = queryResult?.extent ?? null;
  }
  if (extent) {
    await mapView.goTo(extent.expand(1.2));
  }
}

function applyLayerPresentation(
  layerMeta: ActiveDefectLayer,
  tracked: TrackedDefectLayer,
): void {
  tracked.layer.visible = layerMeta.visible;
  tracked.layer.opacity = layerMeta.opacity;
  tracked.layer.title = layerMeta.displayName;
  tracked.layer.renderer = buildDefectRulesRenderer(
    tracked.rules,
    layerMeta.lineWidth,
    layerMeta.lineStyle,
    tracked.geometryKind,
  );
}

function applyHighlightedLayerOrder(
  mapView: View,
  activeLayers: readonly ActiveDefectLayer[],
  tracked: Map<ActiveDefectLayerId, TrackedDefectLayer>,
): void {
  const map = mapView.map;
  if (!map || typeof map.reorder !== 'function') {
    return;
  }

  const nonHighlighted: InstanceType<typeof GeoJSONLayer>[] = [];
  const highlighted: InstanceType<typeof GeoJSONLayer>[] = [];

  for (const layerMeta of activeLayers) {
    const entry = tracked.get(layerMeta.id);
    if (!entry) {
      continue;
    }
    if (layerMeta.highlighted) {
      highlighted.push(entry.layer);
    } else {
      nonHighlighted.push(entry.layer);
    }
  }

  const ordered = [...nonHighlighted, ...highlighted];
  if (ordered.length === 0) {
    return;
  }

  const layersCollection = map.layers as {
    indexOf?: (layer: InstanceType<typeof GeoJSONLayer>) => number;
  };
  const indices = ordered
    .map((layer) =>
      typeof layersCollection.indexOf === 'function'
        ? layersCollection.indexOf(layer)
        : -1,
    )
    .filter((index) => index >= 0);
  if (indices.length === 0) {
    return;
  }

  // Keep active defect overlays above survey / imagery layers.
  const groupTopIndex = Math.max(0, map.layers.length - 1);
  for (const layer of ordered) {
    map.reorder(layer, groupTopIndex);
  }
}

export function useActiveDefectLayersOnMap(view: View | null): void {
  const dispatch = useDispatch<AppDispatch>();
  const activeLayers = useSelector(selectActiveDefectLayers);
  const pendingMapLayerIds = useSelector(selectPendingMapLayerIds);
  const pendingMapLayerIdSet = useMemo(
    () => new Set(pendingMapLayerIds),
    [pendingMapLayerIds],
  );
  const trackedRef = useRef<Map<ActiveDefectLayerId, TrackedDefectLayer>>(new Map());
  const loadingRef = useRef<Set<ActiveDefectLayerId>>(new Set());
  const activeLayersRef = useRef(activeLayers);
  activeLayersRef.current = activeLayers;

  useEffect(() => {
    if (!view?.map) {
      return;
    }

    const tracked = trackedRef.current;
    for (const layerMeta of activeLayers) {
      const entry = tracked.get(layerMeta.id);
      if (entry) {
        applyLayerPresentation(layerMeta, entry);
      }
    }
    applyHighlightedLayerOrder(view, activeLayers, tracked);
  }, [view, activeLayerPresentationSignature(activeLayers)]);

  useEffect(() => {
    if (!view?.map) {
      return;
    }

    const map = view.map;
    const tracked = trackedRef.current;
    for (const layerMeta of activeLayers) {
      const entry = tracked.get(layerMeta.id);
      if (!entry) {
        continue;
      }

      // Feature membership depends on the condition threshold for this preset.
      if (isWorstConditionRuleset(String(layerMeta.rulesetSlug))) {
        map.remove(entry.layer);
        revokeGeoJsonLayerUrl(entry.blobUrl);
        tracked.delete(layerMeta.id);
        continue;
      }

      entry.rules = resolveActiveLayerRules(layerMeta, entry.rules);
      applyLayerPresentation(layerMeta, entry);
    }
    applyHighlightedLayerOrder(view, activeLayers, tracked);
  }, [view, activeLayerVisualizationSignature(activeLayers)]);

  useEffect(() => {
    if (!view?.map) {
      return;
    }

    const map = view.map;
    const activeIds = new Set<ActiveDefectLayerId>(activeLayers.map((layer) => layer.id));
    const tracked = trackedRef.current;
    const offsetByLayerId = assignSameLaneOffsetMetres(activeLayers);

    for (const [layerId, entry] of [...tracked.entries()]) {
      const desiredOffset = offsetByLayerId.get(layerId) ?? 0;
      if (!activeIds.has(layerId) || entry.offsetMetres !== desiredOffset) {
        map.remove(entry.layer);
        revokeGeoJsonLayerUrl(entry.blobUrl);
        tracked.delete(layerId);
      }
    }

    for (const layerMeta of activeLayers) {
      if (isDefectOverlayLayerKind(layerMeta.layerKind)) {
        continue;
      }
      const layerId = layerMeta.id;
      if (tracked.has(layerId)) {
        if (pendingMapLayerIdSet.has(layerId)) {
          dispatch(layerMapRenderSettled(layerId));
        }
        continue;
      }
      if (loadingRef.current.has(layerId)) {
        continue;
      }

      const mapLayerId = toActiveDefectMapLayerId(layerId);
      const offsetMetres = offsetByLayerId.get(layerId) ?? 0;
      loadingRef.current.add(layerId);

      void (async () => {
        let blobUrl: string | undefined;
        const settleMapRender = (): void => {
          dispatch(layerMapRenderSettled(layerId));
        };
        try {
          const [features, rulesBody] = await Promise.all([
            getDefectFeatures(
              layerMeta.defectSlug,
              layerMeta.measurementSlug,
              layerMeta.surveyYearSlug,
              layerMeta.lane || undefined,
            ),
            getDefectRules(layerMeta.defectSlug, layerMeta.rulesetSlug, {
              measurementSlug: layerMeta.measurementSlug,
            }),
          ]);

          const stillActive = activeLayersRef.current.some((layer) => layer.id === layerId);
          if (!stillActive || trackedRef.current.has(layerId)) {
            settleMapRender();
            return;
          }

          if (!features.features?.length) {
            console.warn('SDM defect layer has no features after filter', {
              layerId,
              defectSlug: layerMeta.defectSlug,
              measurementSlug: layerMeta.measurementSlug,
              surveyYearSlug: layerMeta.surveyYearSlug,
              lane: layerMeta.lane,
            });
            settleMapRender();
            return;
          }

          const targetMap = view.map;
          if (!targetMap) {
            settleMapRender();
            return;
          }

          const existingOnMap = targetMap.findLayerById?.(mapLayerId);
          if (existingOnMap) {
            targetMap.remove(existingOnMap);
          }

          // Re-read Redux after awaits so late-arriving visualization settings
          // (e.g. after measure switch rehydration) are applied instead of a
          // stale closed-over layerMeta with null settings.
          const currentMeta =
            activeLayersRef.current.find((layer) => layer.id === layerId) ?? layerMeta;
          const latestOffsets = assignSameLaneOffsetMetres(activeLayersRef.current);
          const resolvedOffsetMetres = latestOffsets.get(layerId) ?? offsetMetres;
          const apiRules = rulesBody.rules;
          const resolvedRules = resolveActiveLayerRules(currentMeta, apiRules);
          const filteredFeatures = resolveActiveLayerFeatures(currentMeta, features);
          if (!filteredFeatures.features?.length) {
            console.warn('SDM defect layer has no features after visualisation filter', {
              layerId,
              defectSlug: layerMeta.defectSlug,
              measurementSlug: layerMeta.measurementSlug,
              surveyYearSlug: layerMeta.surveyYearSlug,
              lane: layerMeta.lane,
              rulesetSlug: layerMeta.rulesetSlug,
            });
            settleMapRender();
            return;
          }

          const offsetFeatures = offsetDefectFeaturesMetres(
            filteredFeatures,
            resolvedOffsetMetres,
          );

          const layerConfig = createDefectGeoJsonLayerConfig(
            mapLayerId,
            currentMeta.displayName,
            offsetFeatures,
            resolvedRules,
            currentMeta.lineWidth,
            currentMeta.lineStyle,
            inferGeometryKindFromFeatures(offsetFeatures),
          );
          blobUrl = layerConfig.blobUrl;

          const geoJsonLayer = new GeoJSONLayer(layerConfig.layerProperties);
          const trackedEntry: TrackedDefectLayer = {
            layer: geoJsonLayer,
            blobUrl,
            rules: resolvedRules,
            geometryKind: layerConfig.geometryKind,
            offsetMetres: resolvedOffsetMetres,
          };
          applyLayerPresentation(currentMeta, trackedEntry);
          targetMap.add(geoJsonLayer);

          if (typeof view.resize === 'function') {
            view.resize();
          }

          await waitForLayerReady(view, geoJsonLayer);

          const stillActiveAfterLoad = activeLayersRef.current.some(
            (layer) => layer.id === layerId,
          );
          if (!stillActiveAfterLoad) {
            targetMap.remove(geoJsonLayer);
            revokeGeoJsonLayerUrl(blobUrl);
            settleMapRender();
            return;
          }

          // Settings may arrive during ArcGIS load; re-resolve from latest Redux.
          const latestMeta =
            activeLayersRef.current.find((layer) => layer.id === layerId) ?? currentMeta;
          trackedEntry.rules = resolveActiveLayerRules(latestMeta, apiRules);
          applyLayerPresentation(latestMeta, trackedEntry);
          trackedRef.current.set(layerId, trackedEntry);
          applyHighlightedLayerOrder(view, activeLayersRef.current, trackedRef.current);
          await zoomMapToLayer(view, geoJsonLayer);
          settleMapRender();
        } catch (error) {
          console.error('Failed to load SDM defect layer on map', layerId, error);
          if (blobUrl) {
            revokeGeoJsonLayerUrl(blobUrl);
          }
          settleMapRender();
        } finally {
          loadingRef.current.delete(layerId);
        }
      })();
    }
  }, [
    view,
    activeLayerDataSignature(activeLayers),
    activeLayerSameLaneOffsetSignature(activeLayers),
    activeLayerVisualizationSignature(activeLayers),
    dispatch,
    pendingMapLayerIdSet,
  ]);

  useEffect(() => {
    const mapView = view;
    return () => {
      const tracked = trackedRef.current;
      if (mapView?.map) {
        for (const entry of tracked.values()) {
          mapView.map.remove(entry.layer);
        }
      }
      for (const entry of tracked.values()) {
        revokeGeoJsonLayerUrl(entry.blobUrl);
      }
      tracked.clear();
      loadingRef.current.clear();
    };
  }, [view]);
}
